import * as THREE from 'three';

// Deterministisches Krater-Muster (kein Math.random, damit die Textur bei jedem Laden
// gleich aussieht) — reiner Canvas-Prozedural-Texture, kein externes Asset nötig.
function makeMoonTexture(): THREE.Texture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#d7d9dc';
  ctx.fillRect(0, 0, size, size);

  let seed = 1337;
  const rng = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  for (let i = 0; i < 70; i++) {
    const x = rng() * size;
    const y = rng() * size;
    const r = 3 + rng() * 16;
    const shade = 150 + Math.floor(rng() * 45);
    ctx.fillStyle = `rgba(${shade},${shade},${shade + 3},0.55)`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  return tex;
}

/**
 * Mond mit echter Phase. Frühere Variante (MeshStandardMaterial, vom Sonnenlicht
 * beleuchtet) sah in der Szene IMMER wie eine gleichmäßig graue Kugel aus: die
 * Ambient-/Hemisphere-Lichter der Szene (zusammen ≈1,45 Intensität) überstrahlen
 * den gerichteten Anteil fast vollständig — der Terminator ist schlicht nicht sichtbar.
 *
 * Deshalb ein eigener Mini-Shader, den Szenenlichter nicht beeinflussen: Er schattiert
 * die Kugel per Lambert-Term gegen die ECHTE Sonnenrichtung (Uniform `sunDir`, gesetzt
 * via setMoonSunDirection aus der Tick-Schleife). Da Sonne UND Mond astronomisch korrekt
 * am Himmel stehen (SunCalc), entspricht der Winkel zwischen ihnen der realen Elongation —
 * Sichel/Halbmond/Vollmond inkl. Orientierung ergeben sich automatisch für jedes Datum.
 * Kosten: eine Kugel, ein Uniform-Update pro Tick — vernachlässigbar.
 */
export class Moon {
  mesh: THREE.Mesh;

  constructor() {
    const geometry = new THREE.SphereGeometry(90, 32, 32);
    const material = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: makeMoonTexture() },
        sunDir: { value: new THREE.Vector3(1, 0, 0) }, // Welt-Richtung Mond → Sonne
      },
      vertexShader: `
        varying vec3 vNormalW;
        varying vec2 vUv;
        void main() {
          vUv = uv;
          vNormalW = normalize(mat3(modelMatrix) * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D map;
        uniform vec3 sunDir;
        varying vec3 vNormalW;
        varying vec2 vUv;
        void main() {
          float lit = max(dot(normalize(vNormalW), normalize(sunDir)), 0.0);
          lit = pow(lit, 0.9); // Terminator minimal weicher ziehen
          // "Erdschein": die Nachtseite bleibt ganz schwach sichtbar, wie am echten Himmel
          float earthshine = 0.07;
          vec3 base = texture2D(map, vUv).rgb;
          gl_FragColor = vec4(base * (earthshine + (1.0 - earthshine) * lit), 1.0);
        }
      `,
    });
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
  }

  addToScene(scene: THREE.Scene) {
    scene.add(this.mesh);
  }

  setPosition(x: number, y: number, z: number) {
    this.mesh.position.set(x, y, z);
  }
}

/** Aktualisiert die Beleuchtungsrichtung für die Mondphase. Erwartet die
 * Weltposition der (Szenen-)Sonne; robust gegen Aufrufe vor der Initialisierung. */
export function setMoonSunDirection(moonMesh: THREE.Mesh, sunWorldPos: THREE.Vector3) {
  const mat = moonMesh.material as THREE.ShaderMaterial;
  const u = mat?.uniforms?.sunDir;
  if (!u?.value?.copy) return;
  u.value.copy(sunWorldPos).sub(moonMesh.position).normalize();
}

/** Position aus Azimut/Elevation, exakt dieselbe Konvention wie SunTracker.posFromAzEl
 * (dort private und bewusst nicht angefasst) — kleine, bewusste Duplikation statt Eingriff
 * in die Sonnen-Logik. */
export function moonPositionFromAzEl(
  azFromNorthDeg: number,
  altDeg: number,
  radius: number,
  worldNorthOffsetDeg: number,
  mirrorEastWest: boolean
): THREE.Vector3 {
  const toRad = THREE.MathUtils.degToRad;
  const a = toRad(azFromNorthDeg + worldNorthOffsetDeg);
  const e = toRad(altDeg);
  const s = mirrorEastWest ? -1 : 1;
  const x = s * radius * Math.sin(a) * Math.cos(e);
  const y = radius * Math.sin(e);
  const z = radius * Math.cos(a) * Math.cos(e);
  return new THREE.Vector3(x, y, z);
}
